import { ImgPath, ImgProps } from '@/shared/ui';

export enum FileType {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
  SVG = 'image/svg+xml',
  HEIF = 'image/heif',
  HEIC = 'image/heic',
  PDF = 'application/pdf',
  ZIP = 'application/zip',
  SIGNATURE = 'application/pkcs7-signature',

  // Creepy file types i was forced to add.
  // Didn't care about naming them properly so used their file formats as enum keys
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ODT = 'application/vnd.oasis.opendocument.text',
  XLS = 'application/vnd.ms-excel',
  ODS = 'application/vnd.oasis.opendocument.spreadsheet',
  PPT = 'application/vnd.ms-powerpoint',
  PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ODP = 'application/vnd.oasis.opendocument.presentation',

  ANY_IMAGE = 'image/*',
  ANY_VIDEO = 'video/*',
  ANY_AUDIO = 'audio/*',
  ANY_TEXT = 'text/*',
}

// TODO: find a way to pass this names from backend

// TODO: learn how to get this value from response headers
export enum FileFormat {
  PNG = '.png',
  JPEG = '.jpeg',
  JPG = '.jpg',
  HEIF = '.heif',
  HEIC = '.heic',
  PDF = '.pdf',
  ZIP = '.zip',
  SIGNATURE = '.p7s',

  DOC = '.doc',
  DOCX = '.docx',
  ODT = '.odt',
  XLS = '.xls',
  ODS = '.ods',
  PPT = '.ppt',
  PPTX = '.pptx',
  ODP = '.odp',
}

export const base64ToPdfBlob = (input: string, type: FileType = FileType.PDF): Blob => {
  const binaryData = base64ToUint8Array(input);

  return new Blob([binaryData.buffer as ArrayBuffer], { type });
};

export const uint8ArrayToBlob = (input: Uint8Array<ArrayBuffer>, type: FileType = FileType.PDF): Blob => {
  return new Blob([input.buffer as ArrayBuffer], { type });
};

export const base64ToUint8Array = (base64: string): Uint8Array<ArrayBuffer> => {
  const binaryString = atob(base64);

  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }
  return uint8Array;
};

export const uint8ArrayToBase64 = (uint8Array: Uint8Array): string => {
  let binaryString = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binaryString);
};

export const fileToBase64 = async (files: File[]): Promise<string[]> => {
  const arrFiles: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const reader = new FileReader();
    const filePromise = new Promise<string>((resolve) => {
      reader.onload = function () {
        const base64String = btoa(reader.result as string);
        resolve(base64String.toString());
      };
    });
    reader.readAsBinaryString(files[i]);
    const base64 = await filePromise;
    arrFiles.push(base64);
  }

  return arrFiles;
};

export async function base64ToFile(base64: string, fileName = 'fileName', fileType = FileType.PNG): Promise<File> {
  const res: Response = await fetch(`data:${fileType};base64,` + base64);
  const blob: Blob = await res.blob();

  return new File([blob], fileName, { type: fileType });
}

export function bytesToSize(bytes: number, floatNumber = 1): string {
  const sizes = ['Байт', 'Кб', 'Мб', 'Гб', 'Тб'];
  if (bytes === 0) return 'n/a';
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(floatNumber ? floatNumber : 1)} ${sizes[i]}`;
}

export const createFilePreview = (file: File): ImgPath => {
  switch (true) {
    case file.type === FileType.PDF:
      return '/images/pdf-icon.svg';

    case file?.type.split('/')[0] === FileType.ANY_VIDEO.split('/')[0]:
      return '/images/video-file.svg';

    default:
      return URL.createObjectURL(file) as ImgPath;
  }
};

type ImageFormat = 'png' | 'jpeg' | 'jpg' | 'gif' | 'svg+xml' | 'webp' | 'heif' | 'heic';
export type ImageBase64 = `data:image/${ImageFormat};base64,${string}`;

export const imageBase64 = (img: string, type: ImageFormat = 'jpeg'): ImageBase64 => {
  return `data:image/${type};base64,${img}`;
};

export const getImageSrc = (img: string, type: ImageFormat = 'jpeg'): ImgProps['src'] => {
  if (!img) {
    return '#' as ImgProps['src'];
  }

  if (img.startsWith('http://') || img.startsWith('https://')) {
    return img as ImgProps['src'];
  }

  if (img.startsWith('data:image/')) {
    return img as ImageBase64;
  }

  return imageBase64(img, type);
};
